import { SOCKET_EVENTS } from "../socketEvents";

/**
 * Presença do atendente (SOCKET_EVENTS.PRESENCE.*) — #4433.
 *
 * Estar online É ter o Markedesk aberto com o socket conectado, em qualquer
 * tela. Não existe heartbeat de aplicação: o socket.io já derruba conexão
 * morta, e o `heartbeat` antigo servia só para empurrar um UPDATE no banco a
 * cada 15s.
 *
 * O backend guarda em memória quem está conectado nele e grava no banco apenas
 * na TRANSIÇÃO (entrou/saiu). Quem lê o estado — Dashboard, ícone de status,
 * NewTicketModal — continua lendo a coluna `online` do usuário, que é
 * compartilhada entre os backends.
 */

/** Usuário como os eventos de presença o entregam. Nunca mais que isto. */
export interface PresenceUserDTO {
  id: number;
  name: string;
  profileImage?: string | null;
  lastSeen?: string | Date | null;
}

/** SOCKET_EVENTS.PRESENCE.USER_ONLINE e USER_OFFLINE. */
export interface PresenceChangedPayload {
  userId: number;
  lastSeen: string | Date;
}

/** SOCKET_EVENTS.PRESENCE.ONLINE_LIST — quem já estava online. */
export type PresenceOnlineListPayload = PresenceUserDTO[];

/** SOCKET_EVENTS.PRESENCE.USER_NEW. */
export interface PresenceUserNewPayload {
  userId: number;
  user: PresenceUserDTO | null;
}

/**
 * Quanto tempo sem renovação o `lastSeen` pode ter e o usuário ainda contar
 * como online.
 *
 * É o conserto do backend que morre sem gravar o `false`: sem este prazo, o
 * atendente ficaria "online" preso para sempre — o defeito de hoje ao
 * contrário. O backend renova o lastSeen dos conectados em lote, bem mais
 * rápido que este limite.
 */
export const PRESENCE_STALE_AFTER_MS = 3 * 60 * 1000;

/** Intervalo da renovação em lote do lastSeen (1 consulta por backend). */
export const PRESENCE_REFRESH_MS = 60 * 1000;

/**
 * Quanto o sistema espera, depois da queda do último socket, antes de concluir
 * que o atendente saiu (#4454 retorno).
 *
 * Presença é o socket conectado, e trocar de aba derruba o socket — sem esta
 * espera, trocar de aba virava fim de sessão na auditoria. Voltar dentro da
 * carência é a MESMA sessão: nada é registrado.
 *
 * Igual ao `PRESENCE_REFRESH_MS` de propósito, que é o carimbo de última
 * instância. O horário da saída vem, em ordem: do próprio fechamento da conexão
 * quando o servidor o observa (fechar a aba, sair — exato); do último pong
 * quando a conexão morre em silêncio e o servidor só descobre pelo ping; e do
 * `lastSeen` se não houve pong nenhum.
 *
 * Confortavelmente abaixo do `PRESENCE_STALE_AFTER_MS` (3 min), então durante a
 * carência o atendente continua lido como online — sem estado intermediário
 * visível na tela.
 */
export const PRESENCE_EXIT_GRACE_MS = 60 * 1000;

/**
 * Regra única de leitura: a coluna sozinha não basta, porque backend derrubado
 * deixa `online: true` para trás. Back e front decidem pelo MESMO cálculo.
 */
export function isUserOnline(
  user: { online?: boolean | null; lastSeen?: string | Date | null },
  agora: Date = new Date()
): boolean {
  if (!user.online) return false;
  if (!user.lastSeen) return false;
  const visto = user.lastSeen instanceof Date ? user.lastSeen : new Date(user.lastSeen);
  if (Number.isNaN(visto.getTime())) return false;
  return agora.getTime() - visto.getTime() <= PRESENCE_STALE_AFTER_MS;
}

export const presenceEvents = SOCKET_EVENTS.PRESENCE;
